import { createSelector } from 'reselect';
import {
  IAppState,
  ITeamData,
  IFrpData,
  IPublicTeamData,
  IMembers,
  IMember,
  IEventData,
} from '../types/state.d';
import { BLANK_RICHEDITOR_STATE } from '../consts';
import { mapMembers, sliceMembers, parseEventDate } from '../utils';
import { getSelectedCause } from './appSelectors';
import { getLocks } from './locksSelectors';
import transformStoryForEditing from '../utils/richTextEditor/transformStoryForEditing';

export const apiSelector = (state: IAppState) => state.api;

export const getTeamData = createSelector(
  apiSelector,
  api => (api.teamData || {}) as ITeamData & IPublicTeamData,
);

export const getTeamStatus = createSelector(
  getTeamData,
  teamData => teamData.status,
);

export const getFundraisingConfiguration = createSelector(
  getTeamData,
  teamData => teamData.fundraisingConfiguration || {},
);

export const getCurrencyCode = createSelector(
  getFundraisingConfiguration,
  fundraisingConfiguration => fundraisingConfiguration.currencyCode,
);

export const getCampaign = createSelector(
  getTeamData,
  teamData => teamData.campaign || {},
);

export const getCharity = createSelector(
  getTeamData,
  teamData => teamData.charity || {},
);

export const getIsGiftAidable = createSelector(
  getCharity,
  getSelectedCause,
  (charity, selectedCause) => {
    const countryCode = selectedCause
      ? selectedCause.countryCode
      : charity.operatingCompanyCode;
    return countryCode === 'GB';
  },
);

export const getTeamCharityId = createSelector(
  getCharity,
  charity => charity.charityId,
);

export type TGetShortName = string | null;
export const getShortName = createSelector(
  getTeamData,
  teamData => (teamData.shortName ? teamData.shortName : ''),
);

export const getTeamFundraisingTarget = createSelector(
  getFundraisingConfiguration,
  fundraisingConfiguration =>
    fundraisingConfiguration.targetAmount
      ? fundraisingConfiguration.targetAmount.toString()
      : '',
);

export const getTeamMembersTarget = createSelector(
  getTeamData,
  teamData => teamData.membersTarget,
);

export const getTeamMembersTargetAmount = createSelector(
  getTeamMembersTarget,
  teamMembersTarget => teamMembersTarget.targetAmount || '',
);

export const isShortNameValid = createSelector(
  apiSelector,
  api => api.isShortNameValid,
);

export const getTeamGuid = createSelector(
  getTeamData,
  teamData => teamData.teamGuid,
);

export const getStory = createSelector(getTeamData, ({ story }) => {
  if (story === null) {
    return BLANK_RICHEDITOR_STATE;
  }
  if (typeof story === 'string') {
    return transformStoryForEditing(JSON.parse(story));
  }
  return story;
});

export type TTeamNameSelector = string;
export const getTeamName = createSelector(
  getTeamData,
  (teamData): TTeamNameSelector => teamData.name || '',
);

export type THeroImageSelector = string | null;
export const getHeroImage = createSelector(
  getTeamData,
  (teamData): THeroImageSelector => teamData.coverPhotoImageId,
);

export const getEventData = createSelector(
  apiSelector,
  api => (api.eventData || {}) as IEventData,
);

export const getEventId = createSelector(
  getTeamData,
  teamData => teamData.event && teamData.event.eventId,
);

export const getEventExpiryDate = createSelector(
  getEventData,
  eventData =>
    (eventData &&
      Boolean(eventData.expiryDate) &&
      parseEventDate(eventData.expiryDate as string)) ||
    null,
);

export const getTotalAmount = createSelector(
  getTeamData,
  teamData =>
    teamData.donationSummary ? teamData.donationSummary.totalAmount : 0,
);

export const getMembershipPolicy = createSelector(
  getTeamData,
  teamData => teamData.membershipPolicy,
);

export const getFundraisingPagesData = createSelector(
  apiSelector,
  api => api.fundraisingPagesData,
);

export const getFrpData = createSelector(
  apiSelector,
  api => (api.frpData || {}) as IFrpData,
);

export const getFrpCoverPhoto = createSelector(
  getFrpData,
  frpData => frpData && frpData.photo,
);

export const getMembersListData = createSelector(
  apiSelector,
  api => api.membersListData,
);

export const getMembersListDataFromTeamData = createSelector(
  getTeamData,
  teamData => teamData.membership,
);

export const getNumberOfMembers = createSelector(
  apiSelector,
  api => api.membersListData.numberOfMembers,
);

export const getNumberOfMembersFromTeamData = createSelector(
  getMembersListDataFromTeamData,
  memberData => memberData.numberOfMembers,
);

export const getCaptain = createSelector(
  getTeamData,
  teamData => teamData.captain || {},
);

export const getCaptainId = createSelector(
  getCaptain,
  captain => captain.userGuid,
);

export const getSortedMembersList = createSelector(
  getMembersListData,
  getCaptainId,
  (membersListData, captainId) => {
    const { members } = membersListData;
    const sortedMembers = members;
    if (sortedMembers) {
      return sortedMembers.sort(
        (member: IMember) => (captainId === member.userGuid ? -1 : 1),
      );
    }
    return [];
  },
);

export const getIsCaptainInMembersList = createSelector(
  getMembersListData,
  getCaptainId,
  (membersListData, captainId) =>
    membersListData.members.reduce((carry, member: IMember) => {
      if (captainId === member.userGuid) {
        return true;
      }
      return carry;
    }, false),
);

export const getSlicedMembersList = (max: number) =>
  createSelector(getMembersListData, ({ members }) =>
    mapMembers(sliceMembers(members, max)),
  );

export const getSlicedMembersListFromTeamData = (max: number) =>
  createSelector(getMembersListDataFromTeamData, ({ members }) =>
    mapMembers(sliceMembers(members, max)),
  );

export const getMembersListExcludingLinkedFRP = createSelector(
  getFrpData,
  getMembersListData,
  (frpData, membersListData) =>
    mapMembers(
      sliceMembers(
        membersListData.members.reduce((carry: IMembers, member) => {
          if (member.fundraisingPageGuid !== frpData.pageGuid) {
            carry.push(member);
          }
          return carry;
        }, []),
        2,
      ),
    ),
);

export const getFrpShortName = createSelector(
  getFrpData,
  frpData => frpData.shortName,
);

export const getGDPRConsents = createSelector(
  apiSelector,
  api => api.GDPRConsents,
);

export const getCharityGDPRConsent = createSelector(
  getGDPRConsents,
  consents =>
    consents
      ? consents.find(consent => consent.entityType === 'Charity')
      : undefined,
);

export const getSelectedCharityId = createSelector(
  getSelectedCause,
  cause => cause!.id,
);

export const getCampaignGuid = createSelector(
  getTeamData,
  getSelectedCause,
  (teamData, selectedCause) => {
    const { campaign } = teamData;
    if (selectedCause) {
      return selectedCause.campaignGuid;
    }
    if (campaign && campaign.campaignGuid) {
      return campaign.campaignGuid;
    }
    return null;
  },
);

export const getCharityId = createSelector(
  getTeamCharityId,
  getSelectedCause,
  (teamId, selectedCause) => (selectedCause && selectedCause.id) || teamId,
);

export const getEventName = createSelector(
  state => Boolean(getLocks(state).event),
  getEventData,
  (isEventLocked, eventData) => (isEventLocked ? eventData.name : undefined),
);
